import React, { FormEvent, useReducer, useState } from 'react';

import { useIsAuthenticated, useMsal } from '@azure/msal-react';
import SubmitBtn from '@components/buttons/FormButtons/SubmitBtn';
import { WarningBox } from '@components/cards/messageBox';
import Checkbox from '@components/inputs/Checkbox';
import WarningModal from '@components/modal/info/Warning';
import { NavBar } from '@components/navigation/NavBar/NavBar';
import Elliptical from '@components/spinners/Elliptical/Elliptical';
import { logger as sentryLogger } from '@sentry/react';
import { UploadParams } from '@services/documentStore/DocumentStore.types';
import DocumentStoreService from '@services/documentStore/DocumentStoreService';
import { IdentityDocumentType, IdentityVerificationDocument, AddDocumentRequest } from '@services/identityVerification/IdentityVerification.types';
import IdentityVerificationService from '@services/identityVerification/IdentityVerificationService';
import { Country } from '@utils/constants/localisation';
import { useNavigate } from 'react-router';
import { useParams } from 'react-router-dom';
import DocumentPanel from '../../components/panels/DocumentPanel';
import { documentDetailInitialState, documentDetailReducer } from './documentDetailReducer';
import { validateDocumentDetail } from './validateDocumentDetail';

/**
 * UpdateDriversLicence allows adding a driver's licence document to an existing identity verification check.
 * This is a simplified version of the full identity verification upload that handles only a single document.
 */
export default function UpdateDriversLicence() {
    const { custId, country } = useParams() as { custId: string, country: Country };

    const navigate = useNavigate();

    const msalContext = useMsal();
    const isAuthenticated = useIsAuthenticated();

    const [declarationChecked, setDeclarationChecked] = useState(false);
    const [isUploading, setIsUploading] = useState(false);
    const [wasSubmitted, setWasSubmitted] = useState(false);
    const [error, setError] = useState(false);
    const [validationErrors, setValidationErrors] = useState<string[]>([]);

    const [licenceDetails, licenceDetailDispatch] = useReducer(
        documentDetailReducer,
        documentDetailInitialState
    );

    const handleSubmit = (event: FormEvent) => {
        setWasSubmitted(true);
        event.preventDefault();

        // Validate input details
        const errors = validateDocumentDetail(
            licenceDetails,
            'Driver Licence',
            country
        );
        if (!declarationChecked) {
            errors.push('Declaration must be checked');
        }
        setValidationErrors(errors);

        if (errors.length === 0) {
            setIsUploading(true);

            // Create request with document
            const document = IdentityVerificationDocument.fromDocumentDetail(licenceDetails);
            const addDocRequest = new AddDocumentRequest(country, custId, document);

            sentryLogger.info('Preparing to add document to existing identity verification check:', { addDocRequest });
            console.log('Preparing to upload document:', addDocRequest);

            IdentityVerificationService.addDocumentToExistingCheck(
                addDocRequest,
                { msalContext, isAuthenticated }
            )
                .then(preparationResponse => {
                    sentryLogger.info(`Document added to check ${preparationResponse.identity_verification_check_reference_id}, response: ${JSON.stringify(preparationResponse)}`);

                    // Validation ensures both files exist for driver's licence
                    const documentUrls = preparationResponse.presigned_upload_urls;
                    if (!documentUrls.front_file || !documentUrls.back_file) {
                        sentryLogger.error(`Missing presigned URLs in preparation response, documentUrls: ${JSON.stringify(documentUrls)}`);
                        throw new Error('Missing upload URLs from backend');
                    }

                    const allUploads: UploadParams[] = [
                        {
                            presignedUrl: documentUrls.front_file.presigned_upload_url,
                            file: licenceDetails.frontFile!,
                            contentType: licenceDetails.frontFile!.type
                        },
                        {
                            presignedUrl: documentUrls.back_file.presigned_upload_url,
                            file: licenceDetails.backFile!,
                            contentType: licenceDetails.backFile!.type
                        }
                    ];

                    sentryLogger.info(`Starting upload of ${allUploads.length} files for check: ${preparationResponse.identity_verification_check_reference_id}`);
                    return DocumentStoreService.uploadFiles(allUploads)
                        .then(uploadResults => {
                            const failedUploads = uploadResults.filter(result => !result.success);

                            if (failedUploads.length > 0) {
                                sentryLogger.error(`${failedUploads.length} file uploads failed for check: ${preparationResponse.identity_verification_check_reference_id}, failures: ${JSON.stringify(failedUploads)}`);
                                console.error(`${failedUploads.length} uploads failed:`, failedUploads);
                                setError(true);
                                setWasSubmitted(false);
                                setIsUploading(false);
                            } else {
                                sentryLogger.info(`Document upload for check ${preparationResponse.identity_verification_check_reference_id} completed successfully`);
                                console.log('All uploads completed successfully');
                                // Return to previous view
                                navigate(-1);
                            }
                        });
                })
                .catch(error => {
                    sentryLogger.error(`Failed to update document on existing check: ${error}`);
                    console.error('Failed to update document:', error);
                    setError(true);
                    setWasSubmitted(false);
                    setIsUploading(false);
                });
        } else {
            setWasSubmitted(false);
        }
    };

    return (
        <>
            <NavBar pageTitle='Update Driver Licence' />
            {isUploading &&
                <div
                    data-testid="loading-spinner"
                    style={{ display: 'flex', justifyContent: 'center', margin: '15vh' }}
                >
                    <Elliptical />
                </div>
            }
            {!isUploading &&
                <form style={{ margin: '2.5vh 2.5vw' }} onSubmit={handleSubmit}>
                    {
                        validationErrors.length > 0 &&
                        <WarningBox
                            testId='validation-errors'
                            message={
                                <>
                                    <div>Please fix the following errors before submitting:</div>
                                    <ul style={{ marginTop: '0.5rem', marginBottom: 0, paddingLeft: '1.5rem' }}>
                                        {validationErrors.map((error, index) => (
                                            <li key={index}>{error}</li>
                                        ))}
                                    </ul>
                                </>
                            }
                        />
                    }

                    <DocumentPanel
                        testId="driver-licence-panel"
                        title='Driver Licence'
                        availableDocumentTypes={[IdentityDocumentType.DRIVER_LICENCE]}
                        documentDetail={licenceDetails}
                        documentDetailDispatch={licenceDetailDispatch}
                        country={country}
                    />

                    <div data-testid="declaration-panel" style={{ margin: '2.5vh 0' }}>
                        <Checkbox
                            id="declaration"
                            label={`I, ${msalContext.accounts.at(0)?.name}, ` +
                                `declare that the customer identity has been verified by me.`}
                            required
                            onChange={setDeclarationChecked}
                        />
                    </div>

                    <div style={{ margin: '2.5vh 0' }}>
                        <SubmitBtn
                            text='Submit'
                            testId='submit-button'
                            disabled={wasSubmitted}
                        />
                    </div>
                </form>
            }
            {
                error &&
                <WarningModal
                    message='Something went wrong'
                    additionalInfo='We’re sorry, we were unable to process your request. Please try again later and if the issue persists, please log a ticket with IT Helpdesk.'
                    onClickClose={(event: any) => {
                        event.preventDefault();
                        setError(false);
                        window.location.reload();
                    }} />
            }
        </>
    );
}
